// src/pages/BlogArticle.js
import React, { useEffect, useState, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase";
import MDEditor from "@uiw/react-md-editor";
import { useParams } from "react-router-dom";

// Lazy loading i fullscreen
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { motion, AnimatePresence } from "framer-motion";

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);

  const [fullscreenImage, setFullscreenImage] = useState(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    const fetchArticle = async () => {
      const snap = await getDocs(collection(db, "blog"));
      const posts = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const found = posts.find((p) => p.slug === slug);
      setArticle(found || null);
    };

    fetchArticle();
  }, [slug]);


  // Lepszy efekt masonry — adaptacja z PanelPhotos (obsługuje lazyload, resize, błędy)
  useEffect(() => {
    if (!galleryRef.current) return;
    const grid = galleryRef.current;

    const getRowHeight = () =>
      parseFloat(getComputedStyle(grid).getPropertyValue("grid-auto-rows")) || 8;
    const getRowGap = () => {
      const style = getComputedStyle(grid);
      return (
        parseFloat(style.getPropertyValue("row-gap")) ||
        parseFloat(style.getPropertyValue("gap")) ||
        8
      );
    };

    const getRenderedImgHeight = (img) => {
      if (!img.naturalWidth || !img.naturalHeight) {
        return img.getBoundingClientRect().height || 0;
      }
      const displayWidth = img.clientWidth;
      const renderedHeight = (img.naturalHeight / img.naturalWidth) * displayWidth;
      return renderedHeight;
    };

    const resizeItem = (item) => {
      if (!item) return;
      const img = item.querySelector("img");
      if (!img) return;
      if (!img.naturalWidth || !img.naturalHeight) return;

      const rowHeight = getRowHeight();
      const rowGap = getRowGap();
      const height = getRenderedImgHeight(img);
      const span = Math.ceil((height + rowGap) / (rowHeight + rowGap));
      item.style.gridRowEnd = `span ${span}`;

      img.style.opacity = "1";
    };

    const resizeAll = () => {
      const items = grid.querySelectorAll(".gallery-item");
      items.forEach((it) => resizeItem(it));
    };

    // Debounce helper dla resize okna
    let resizeTimer = null;
    const onWinResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        requestAnimationFrame(resizeAll);
      }, 120);
    };
    window.addEventListener("resize", onWinResize);

    // MutationObserver — gdy DOM się zmienia (lazyload itp.)
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(resizeAll);
    });
    mutationObserver.observe(grid, { childList: true, subtree: true });

    // Podłączamy load listener do obrazków
    const observeImages = () => {
      const imgs = grid.querySelectorAll("img");
      imgs.forEach((img) => {
        if (!img.style.opacity) img.style.opacity = "0";
        const parent = img.closest(".gallery-item");

        const onLoad = () => {
          setTimeout(() => {
            resizeItem(parent);
          }, 20);
        };

        if (img.complete && img.naturalWidth && img.naturalHeight) {
          onLoad();
        } else {
          img.addEventListener("load", onLoad, { once: true });
          img.addEventListener(
            "error",
            () => {
              if (parent) {
                parent.style.gridRowEnd = `span ${Math.ceil(200 / (getRowHeight() + getRowGap()))}`;
                img.style.opacity = "1";
              }
            },
            { once: true }
          );
        }
      });
    };

    // initial
    observeImages();
    resizeAll();

    // obserwuj zmianę src (np. lazyload podmienia src)
    const attrObserver = new MutationObserver((mutList) => {
      for (const m of mutList) {
        if (m.type === "attributes" && m.attributeName === "src") {
          const img = m.target;
          if (img && img.tagName === "IMG") {
            if (img.complete && img.naturalWidth && img.naturalHeight) {
              const parent = img.closest(".gallery-item");
              resizeItem(parent);
            } else {
              img.addEventListener(
                "load",
                () => {
                  const parent = img.closest(".gallery-item");
                  resizeItem(parent);
                },
                { once: true }
              );
            }
          }
        }
      }
    });
    attrObserver.observe(grid, { subtree: true, attributes: true, attributeFilter: ["src"] });

    // failsafe
    const t1 = setTimeout(() => requestAnimationFrame(resizeAll), 1000);
    const t2 = setTimeout(() => requestAnimationFrame(resizeAll), 2500);

    return () => {
      window.removeEventListener("resize", onWinResize);
      mutationObserver.disconnect();
      attrObserver.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [article?.gallery]);

  if (!article) return <p className="p-6">Ładowanie artykułu...</p>;

  // helper: normalizuj element galerii -> url i alt
  const normalizeGalleryItem = (item, index) => {
    if (!item) return { url: "", alt: `Zdjęcie ${index + 1}` };
    if (typeof item === "string") return { url: item, alt: `Zdjęcie ${index + 1}` };
    return {
      url: item.url || item.path || item.src || "",
      alt: item.alt || item.name || `Zdjęcie ${index + 1}`,
    };
  };

  return (
    <div className="mx-auto p-6">
      {/* Okładka */}
      <div className="max-w-4xl mx-auto mt-20">
        {article.coverUrl && (
        <img
          src={article.coverUrl}
          alt={article.title}
          className="w-full rounded-lg mb-6 shadow"
        />
      )}

      {/* Tytuł */}
      <h1 className="text-4xl font-bold mb-3">{article.title}</h1>

      {/* Data */}
      {article.createdAt?.seconds && (
        <p className="text-gray-600 mb-6">
          📅{" "}
          {new Date(article.createdAt.seconds * 1000).toLocaleDateString(
            "pl-PL",
            { year: "numeric", month: "long", day: "numeric" }
          )}
        </p>
      )}

      {/* Markdown */}
      <div data-color-mode="light">
        <MDEditor.Markdown source={article.content} />
      </div>
      </div>

      {/* Galeria */}
      {article.gallery?.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-semibold mb-4">Galeria</h2>

          <div
            ref={galleryRef}
            id="gallery"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full items-start"
            style={{
              gridAutoRows: "8px",
              gridAutoFlow: "dense",
            }}
          >
            {article.gallery.map((item, index) => {
              const { url, alt } = normalizeGalleryItem(item, index);
              return (
                <div
                  key={index}
                  className="gallery-item w-full rounded-lg shadow cursor-zoom-in transition-shadow duration-300"
                  onClick={() => setFullscreenImage(url)}
                >
                  {url ? (
                    <LazyLoadImage
                      src={url}
                      alt={alt}
                      effect="blur"
                      className="w-full h-auto object-contain block opacity-0 transition-opacity duration-500"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center rounded-lg">
                      <p className="text-sm text-gray-500">Podgląd niedostępny</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fullscreen podgląd */}
      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            key="fullscreen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center cursor-pointer"
            onClick={() => setFullscreenImage(null)}
          >
            <img
              src={fullscreenImage}
              alt=""
              className="max-w-full max-h-full object-contain cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-4 left-4 text-white text-3xl font-bold hover:text-gray-300 transition cursor-pointer"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
